import * as THREE from 'three'
import {
  Emitter,
  parseAnchorSpec,
  TrackingState,
  type AnchorSpec,
  type FaceFrame,
  type POSE_SMOOTHER_PRESETS,
  type PoseSmootherConfig,
  type TrackingSnapshot,
  type TrackingStateOptions,
} from '@ar-suite/core'
import { CameraManager, EarTracker, FaceTracker, FrameLoop, HandTracker, PoseTracker, type CameraFacing, type CameraOptions, type EarModelContract, type EarTrackerResult, type FaceTrackerOptions } from '@ar-suite/tracking-web'
import { ARScene, captureComposite, DebugOverlay2D, FaceOccluder, GLBLoader, type CaptureOptions } from '@ar-suite/render-web'
import { createProceduralGlasses, GlassesModule, PROCEDURAL_PRODUCTS, type ProceduralGlassesOptions } from '@ar-suite/module-glasses'
import { createProceduralEarring, EarringsModule, PROCEDURAL_EARRINGS, type ProceduralEarringOptions } from '@ar-suite/module-earrings'
import { createProceduralFaceProp, FaceAnchorModule, PROCEDURAL_FACE_PROPS, type ProceduralFacePropOptions } from '@ar-suite/module-face-props'
import { createProceduralNecklace, NecklaceModule, PROCEDURAL_NECKLACES, type ProceduralNecklaceOptions } from '@ar-suite/module-necklace'
import { createProceduralRing, RingModule, PROCEDURAL_RINGS, type ProceduralRingOptions } from '@ar-suite/module-ring'
import { createProceduralWrist, WristModule, PROCEDURAL_WRIST, type ProceduralWristOptions } from '@ar-suite/module-wrist'

/** 'jewelry' = nose pin / bindi / maang tikka; 'hats' = hats & headphones. */
/** 'rings' = finger rings; 'wrist' = bangles, bracelets & watches. */
export type ModuleName = 'glasses' | 'earrings' | 'jewelry' | 'hats' | 'necklace' | 'rings' | 'wrist' // | 'shoe' (Phase 6)
const FACE_PROP_CATEGORIES: Record<string, ModuleName> = { nosepin: 'jewelry', bindi: 'jewelry', tikka: 'jewelry', hat: 'hats' }

/** A resolved product: what the studio/API returns for a productId. */
export interface ProductDescriptor {
  id: string
  name?: string
  glbUrl?: string
  /** anchor.json contents (object or already-parsed spec). */
  anchor: unknown | AnchorSpec
  /** Built-in procedural demo model (used when glbUrl is absent); shape depends on category. */
  procedural?:
    | Partial<ProceduralGlassesOptions>
    | Partial<ProceduralEarringOptions>
    | Partial<ProceduralFacePropOptions>
    | Partial<ProceduralNecklaceOptions>
    | Partial<ProceduralRingOptions>
    | Partial<ProceduralWristOptions>
  variantId?: string
}

export interface ARSuiteInitOptions {
  apiKey?: string
  modules: ModuleName[]
  /** Self-hosted asset locations. */
  assets?: { wasmPath?: string; faceModelPath?: string; poseModelPath?: string; earModelPath?: string; earModelContract?: EarModelContract; dracoPath?: string; ktx2Path?: string }
  camera?: Partial<CameraOptions>
  tracker?: Partial<FaceTrackerOptions>
  tracking?: Partial<TrackingStateOptions>
  smoothing?: PoseSmootherConfig | keyof typeof POSE_SMOOTHER_PRESETS
  /** Resolve a productId to a descriptor (defaults to the built-in procedural catalog + Phase-4 API later). */
  resolveProduct?: (productId: string) => Promise<ProductDescriptor | null> | ProductDescriptor | null
  /** Vertical FOV of the renderer/tracker camera model. Keep 63 for MediaPipe. */
  fovYDeg?: number
  maxPixelRatio?: number
}

export type TryOnRequest =
  | { productId: string; variantId?: string }
  | { glbUrl: string; anchor: unknown | AnchorSpec; variantId?: string }
  | { product: ProductDescriptor }

export interface ARSuiteStats {
  fps: number
  inferenceMs: number
  renderMs: number
  frames: number
  loopMode: 'rvfc' | 'raf' | 'none'
}

export interface DebugFlags {
  landmarks?: boolean
  tesselation?: boolean
  occluder?: boolean
  anchors?: boolean
  axes?: boolean
}

export interface ARSuiteEvents extends Record<string, unknown> {
  ready: undefined
  tracking: TrackingSnapshot
  /** Fired after every processed camera frame (raw face frame or null). */
  frame: { face: FaceFrame | null; timestamp: number }
  error: Error
  productLoaded: { id: string }
  destroyed: undefined
}

/**
 * Public web SDK facade. Same surface as the native SDKs (Phase 5):
 * init → mount → tryOn → on('tracking') → capture → destroy.
 */
export class ARSuite extends Emitter<ARSuiteEvents> {
  readonly camera: CameraManager
  readonly tracker: FaceTracker
  /** Our own ear keypoint detector (null when the earrings module is off or the model failed to load). */
  readonly earTracker: EarTracker | null = null
  readonly scene: ARScene
  readonly occluder: FaceOccluder
  readonly tracking: TrackingState
  readonly glasses: GlassesModule | null = null
  readonly earrings: EarringsModule | null = null
  /** Generic module for rigid face-anchored props (nose pin / bindi / tikka / hats / headphones). */
  readonly faceProps: FaceAnchorModule | null = null
  readonly necklace: NecklaceModule | null = null
  readonly rings: RingModule | null = null
  readonly wrist: WristModule | null = null
  /** Hand landmarker — created when rings/wrist modules are enabled. */
  readonly handTracker: HandTracker | null = null
  /** Body pose (shoulders) — created when a module needs it (necklaces now; watches later). */
  readonly poseTracker: PoseTracker | null = null
  readonly debugOverlay: DebugOverlay2D
  readonly loader: GLBLoader
  private options: ARSuiteInitOptions
  private container: HTMLElement | null = null
  private loop: FrameLoop | null = null
  private lastFace: FaceFrame | null = null
  private lastEars: EarTrackerResult | null = null
  private lastPose: import('@ar-suite/core').PoseFrame | null = null
  private poseFrameCounter = 0
  private lastHands: import('@ar-suite/core').HandFrame[] = []
  private earBusy = false
  private _stats: ARSuiteStats = { fps: 0, inferenceMs: 0, renderMs: 0, frames: 0, loopMode: 'none' }
  private fpsWindow: number[] = []
  private debug: DebugFlags = {}
  private currentProductId: string | null = null
  private destroyed = false

  private constructor(options: ARSuiteInitOptions) {
    super()
    this.options = options
    const assets = options.assets ?? {}
    this.camera = new CameraManager(options.camera)
    this.tracker = new FaceTracker({
      wasmPath: assets.wasmPath ?? '/wasm',
      modelPath: assets.faceModelPath ?? '/models/face_landmarker.task',
      ...options.tracker,
    })

    this.scene = new ARScene({
      fovYDeg: options.fovYDeg ?? 63,
      mirror: (options.camera?.facing ?? 'user') === 'user',
      maxPixelRatio: options.maxPixelRatio ?? 2,
    })
    this.loader = new GLBLoader({ dracoPath: assets.dracoPath, ktx2Path: assets.ktx2Path, renderer: this.scene.renderer })
    this.occluder = new FaceOccluder()
    this.scene.occluders.add(this.occluder.group)
    this.tracking = new TrackingState(options.tracking)
    this.tracking.on('change', (s) => this.emit('tracking', s))
    this.tracking.on('lost', () => {
      this.glasses?.onTrackingLost()
      this.earrings?.onTrackingLost()
      this.faceProps?.onTrackingLost()
      this.necklace?.onTrackingLost()
    })
    // hand-worn modules track hands, not the face: face loss does not hide them
    this.debugOverlay = new DebugOverlay2D(FaceTracker.tesselation)
    if (options.modules.includes('glasses')) {
      ;(this as { glasses: GlassesModule | null }).glasses = new GlassesModule(this.scene, { smoothing: options.smoothing })
      this.glasses!.attachOccluder(this.occluder)
    }
    if (options.modules.includes('earrings')) {
      ;(this as { earrings: EarringsModule | null }).earrings = new EarringsModule(this.scene, { smoothing: options.smoothing })
      this.earrings!.attachOccluder(this.occluder)
      ;(this as { earTracker: EarTracker | null }).earTracker = new EarTracker({
        modelPath: assets.earModelPath ?? '/models/earnet.onnx',
        contract: assets.earModelContract ?? 'earnet',
      })
    }
    if (options.modules.includes('necklace')) {
      ;(this as { necklace: NecklaceModule | null }).necklace = new NecklaceModule(this.scene, { smoothing: options.smoothing })
      this.necklace!.attachOccluder(this.occluder)
      ;(this as { poseTracker: PoseTracker | null }).poseTracker = new PoseTracker({
        wasmPath: assets.wasmPath ?? '/wasm',
        modelPath: assets.poseModelPath ?? '/models/pose_landmarker_lite.task',
        delegate: options.tracker?.delegate ?? 'GPU',
      })
    }
    if (options.modules.includes('rings') || options.modules.includes('wrist')) {
      ;(this as { handTracker: HandTracker | null }).handTracker = new HandTracker({
        wasmPath: assets.wasmPath ?? '/wasm',
        modelPath: '/models/hand_landmarker.task',
        delegate: options.tracker?.delegate ?? 'GPU',
      })
      if (options.modules.includes('rings'))
        (this as { rings: RingModule | null }).rings = new RingModule(this.scene, { smoothing: options.smoothing })
      if (options.modules.includes('wrist')) {
        ;(this as { wrist: WristModule | null }).wrist = new WristModule(this.scene, { smoothing: options.smoothing })
        if (!this.poseTracker) {
          ;(this as { poseTracker: PoseTracker | null }).poseTracker = new PoseTracker({
            wasmPath: assets.wasmPath ?? '/wasm',
            modelPath: assets.poseModelPath ?? '/models/pose_landmarker_lite.task',
            delegate: options.tracker?.delegate ?? 'GPU',
          })
        }
      }
    }
    if (options.modules.includes('jewelry') || options.modules.includes('hats')) {
      ;(this as { faceProps: FaceAnchorModule | null }).faceProps = new FaceAnchorModule(this.scene, { smoothing: options.smoothing })
      this.faceProps!.attachOccluder(this.occluder)
    }
  }

  /** Create the SDK and load the tracking model (does not touch the camera yet). */
  static async init(options: ARSuiteInitOptions): Promise<ARSuite> {
    const ar = new ARSuite(options)
    const earInit = ar.earTracker?.init().catch((e: unknown) => {
      console.warn('[ar-suite] ear tracker unavailable, earrings fall back to face-mesh anchoring', e)
      ;(ar as { earTracker: EarTracker | null }).earTracker = null
    })
    await Promise.all([ar.tracker.init(), earInit, ar.poseTracker?.init(), ar.handTracker?.init()])
    ar.emit('ready', undefined)
    return ar
  }

  get stats(): ARSuiteStats {
    return this._stats
  }
  get mirrored(): boolean {
    return this.camera.mirrored
  }
  get productId(): string | null {
    return this.currentProductId
  }

  /** Attach video + canvases to a container, start the camera and the tracking loop. */
  async mount(container: HTMLElement): Promise<void> {
    this.container = container
    this.scene.mount(container, this.camera.video)
    this.debugOverlay.mount(container, this.camera.mirrored)
    await this.camera.start()
    this.onCameraStarted()
    this.camera.on('start', () => this.onCameraStarted())
  }

  private onCameraStarted(): void {
    const w = this.camera.width, h = this.camera.height
    this.scene.resize(w, h)
    this.debugOverlay.resize(w, h)
    this.scene.setMirror(this.camera.mirrored)
    this.debugOverlay.setMirror(this.camera.mirrored)
    this.loop?.stop()
    this.loop = new FrameLoop(this.camera.video, (tick) => this.step(tick.timestamp, tick.newFrame))
    this.loop.start()
    this._stats.loopMode = this.loop.mode
  }

  async switchCamera(facing?: CameraFacing): Promise<void> {
    if (facing) await this.camera.start({ facing, deviceId: undefined })
    else await this.camera.switchFacing()
  }

  /** Load and show a product. */
  async tryOn(req: TryOnRequest): Promise<void> {
    let product: ProductDescriptor | null
    if ('product' in req) product = req.product
    else if ('glbUrl' in req) product = { id: req.glbUrl, glbUrl: req.glbUrl, anchor: req.anchor, variantId: req.variantId }
    else {
      product = (await this.options.resolveProduct?.(req.productId)) ?? defaultResolveProduct(req.productId)
      if (!product) throw new Error(`Unknown product "${req.productId}" (no resolveProduct configured)`)
      if (req.variantId) product = { ...product, variantId: req.variantId }
    }
    // always normalise: fills category defaults, validates, and copies (so callers' objects are not mutated by fit sliders)
    const spec = parseAnchorSpec(product.anchor)
    const variant = product.variantId ? spec.variants.find((v) => v.id === product!.variantId) : undefined
    const glbUrl = variant?.glbUrl ?? product.glbUrl
    const loadGlb = async (): Promise<THREE.Object3D | null> =>
      glbUrl ? (await this.loader.load(glbUrl, spec.unitScale)).root : null

    if (spec.category === 'glasses') {
      if (!this.glasses) throw new Error('glasses module not enabled in init({ modules })')
      const model = (await loadGlb()) ?? createProceduralGlasses((product.procedural ?? {}) as Partial<ProceduralGlassesOptions>)
      this.clearAllModels('glasses')
      this.glasses.setSpec(spec)
      this.glasses.setModel(model)
      if (variant?.materials) this.glasses.applyVariantMaterials(variant.materials)
    } else if (spec.category === 'earring') {
      if (!this.earrings) throw new Error('earrings module not enabled in init({ modules })')
      let model = await loadGlb()
      let hangLength: number | undefined
      if (!model) {
        const made = createProceduralEarring((product.procedural ?? {}) as Partial<ProceduralEarringOptions>)
        model = made.group
        hangLength = made.hangLengthCm
      }
      this.clearAllModels('earrings')
      this.earrings.setSpec(spec)
      this.earrings.setModel(model, hangLength)
      if (variant?.materials) this.earrings.applyVariantMaterials(variant.materials)
    } else if (spec.category in FACE_PROP_CATEGORIES) {
      const need = FACE_PROP_CATEGORIES[spec.category]!
      if (!this.faceProps || !this.options.modules.includes(need))
        throw new Error(`"${need}" module not enabled in init({ modules })`)
      const model = (await loadGlb()) ?? createProceduralFaceProp((product.procedural ?? {}) as Partial<ProceduralFacePropOptions>)
      this.clearAllModels('faceProps')
      this.faceProps.setSpec(spec)
      this.faceProps.setModel(model)
      if (variant?.materials) this.faceProps.applyVariantMaterials(variant.materials)
    } else if (spec.category === 'necklace') {
      if (!this.necklace) throw new Error('necklace module not enabled in init({ modules })')
      const model = (await loadGlb()) ?? createProceduralNecklace((product.procedural ?? {}) as Partial<ProceduralNecklaceOptions>)
      this.clearAllModels('necklace')
      this.necklace.setSpec(spec)
      this.necklace.setModel(model)
      if (variant?.materials) this.necklace.applyVariantMaterials(variant.materials)
    } else if (spec.category === 'ring') {
      if (!this.rings) throw new Error('rings module not enabled in init({ modules })')
      const model = (await loadGlb()) ?? createProceduralRing((product.procedural ?? {}) as Partial<ProceduralRingOptions>)
      this.clearAllModels('rings')
      this.rings.setSpec(spec)
      this.rings.setModel(model)
      if (variant?.materials) this.rings.applyVariantMaterials(variant.materials)
    } else if (spec.category === 'bracelet' || spec.category === 'watch') {
      if (!this.wrist) throw new Error('wrist module not enabled in init({ modules })')
      const model = (await loadGlb()) ?? createProceduralWrist((product.procedural ?? {}) as Partial<ProceduralWristOptions>)
      this.clearAllModels('wrist')
      this.wrist.setSpec(spec)
      this.wrist.setModel(model)
      if (variant?.materials) this.wrist.applyVariantMaterials(variant.materials)
    } else {
      throw new Error(`Category "${spec.category}" is not enabled yet (Phase 1+)`)
    }
    this.currentProductId = product.id
    this.emit('productLoaded', { id: product.id })
  }

  private clearAllModels(except?: 'glasses' | 'earrings' | 'faceProps' | 'necklace' | 'rings' | 'wrist'): void {
    if (except !== 'glasses') this.glasses?.setModel(null)
    if (except !== 'earrings') this.earrings?.setModel(null)
    if (except !== 'faceProps') this.faceProps?.setModel(null)
    if (except !== 'necklace') this.necklace?.setModel(null)
    if (except !== 'rings') this.rings?.setModel(null)
    if (except !== 'wrist') this.wrist?.setModel(null)
  }

  /** Remove the current product. */
  clear(): void {
    this.clearAllModels()
    this.currentProductId = null
  }

  /** Apply an edited spec (fit sliders) to whichever module wears its category. */
  updateSpec(spec: AnchorSpec): void {
    if (spec.category === 'glasses') this.glasses?.setSpec(spec)
    else if (spec.category === 'earring') this.earrings?.setSpec(spec)
    else if (spec.category === 'necklace') this.necklace?.setSpec(spec)
    else if (spec.category === 'ring') this.rings?.setSpec(spec)
    else if (spec.category === 'bracelet' || spec.category === 'watch') this.wrist?.setSpec(spec)
    else this.faceProps?.setSpec(spec)
  }

  setDebug(flags: DebugFlags): void {
    this.debug = { ...this.debug, ...flags }
    this.occluder.debug = !!this.debug.occluder
    this.debugOverlay.showLandmarks = !!this.debug.landmarks
    this.debugOverlay.showTesselation = !!this.debug.tesselation
    if (!this.debug.landmarks && !this.debug.tesselation && !this.debug.anchors && !this.debug.axes) this.debugOverlay.clear()
  }

  /** Composite photo of video + wearable (mirrored like the preview). */
  async capture(options: CaptureOptions = {}): Promise<Blob> {
    this.scene.render()
    return captureComposite(this.camera.video, this.scene.canvas, { mirror: this.camera.mirrored, ...options })
  }

  private step(timestamp: number, newFrame: boolean): void {
    if (this.destroyed || !this.camera.running) return
    if (this.camera.width !== this.scene.width || this.camera.height !== this.scene.height) {
      this.scene.resize(this.camera.width, this.camera.height)
      this.debugOverlay.resize(this.camera.width, this.camera.height)
    }
    const t0 = performance.now()
    let face: FaceFrame | null = this.lastFace
    if (newFrame) {
      try {
        const res = this.tracker.detect(this.camera.video, timestamp)
        face = res.faces[0] ?? null
        let poseMs = 0
        const needPose = this.necklace?.hasModel || this.wrist?.hasModel
        if (this.poseTracker?.ready && needPose && this.poseFrameCounter++ % 2 === 0) {
          const pr = this.poseTracker.detect(this.camera.video, timestamp)
          this.lastPose = pr.pose
          poseMs = pr.inferenceMs
        }
        let handMs = 0
        if (this.handTracker?.ready && (this.rings?.hasModel || this.wrist?.hasModel)) {
          const hr = this.handTracker.detect(this.camera.video, timestamp)
          this.lastHands = hr.hands
          handMs = hr.inferenceMs
        }
        this._stats.inferenceMs = this._stats.inferenceMs * 0.9 + (res.inferenceMs + poseMs + handMs) * 0.1
      } catch (e) {
        this.emit('error', e instanceof Error ? e : new Error(String(e)))
        face = null
      }
      this.lastFace = face
    }
    const quality = face ? faceQuality(face) : 0
    const snap = this.tracking.update(!!face, quality, timestamp)

    if (face && snap.status === 'tracking' && !snap.holding) {
      if (this.glasses?.hasModel) this.glasses.update(face, this.scene.model, timestamp)
      if (this.faceProps?.hasModel) this.faceProps.update(face, this.scene.model, timestamp)
      if (this.necklace?.hasModel) this.necklace.update(face, this.scene.model, timestamp, this.lastPose)
      if (this.earrings?.hasModel) {
        // ear detection is async (wasm) — kick it off and consume the latest result next frame
        if (this.earTracker?.ready && !this.earBusy && newFrame) {
          this.earBusy = true
          this.earTracker
            .detect(this.camera.video, face, this.scene.model)
            .then((r) => (this.lastEars = r))
            .catch(() => undefined)
            .finally(() => (this.earBusy = false))
        }
        const ears = this.lastEars
        this.earrings.update(face, this.scene.model, timestamp, ears && {
          left: ears.left && { ...ears.left.points.lobe_attach, confidence: ears.left.confidence },
          right: ears.right && { ...ears.right.points.lobe_attach, confidence: ears.right.confidence },
        })
      }
      this.occluder.update(face, this.scene.model)
    } else if (snap.status !== 'tracking') {
      if (this.glasses) this.glasses.visible = false
      if (this.earrings) this.earrings.visible = false
      if (this.faceProps) this.faceProps.visible = false
      if (this.necklace) this.necklace.visible = false
    }
    // hand-worn wearables track hands, not the face — update regardless of face state
    if (this.rings?.hasModel) this.rings.update(this.lastHands, this.scene.model, timestamp)
    if (this.wrist?.hasModel) this.wrist.update(this.lastHands, this.scene.model, timestamp, this.lastPose)

    const t1 = performance.now()
    this.scene.render()
    this._stats.renderMs = this._stats.renderMs * 0.9 + (performance.now() - t1) * 0.1

    if (this.debug.landmarks || this.debug.tesselation || this.debug.anchors || this.debug.axes) {
      this.debugOverlay.clear()
      if (face && snap.status === 'tracking') {
        if (this.debug.landmarks || this.debug.tesselation) this.debugOverlay.drawFace(face, this.debug.anchors ? [168, 234, 454, 1, 152, 10] : [])
        const r = this.glasses?.lastResult
        if (this.debug.anchors && r) this.debugOverlay.drawPoint(r.pose.position, this.scene.model, '#ffcc00')
        if (this.debug.axes && r) {
          const m = face.headMatrix
          const o: [number, number, number] = [m[12]!, m[13]!, m[14]!]
          const ax = (i: number): [number, number, number] => [o[0] + m[i]! * 5, o[1] + m[i + 1]! * 5, o[2] + m[i + 2]! * 5]
          this.debugOverlay.drawAxes(o, [ax(0), ax(4), ax(8)], this.scene.model)
        }
      }
    }
    // stats
    this._stats.frames++
    this.fpsWindow.push(t0)
    while (this.fpsWindow.length && t0 - this.fpsWindow[0]! > 1000) this.fpsWindow.shift()
    this._stats.fps = this.fpsWindow.length
    this.emit('frame', { face: snap.status === 'tracking' ? face : null, timestamp })
  }

  destroy(): void {
    if (this.destroyed) return
    this.destroyed = true
    this.loop?.stop()
    this.camera.stop()
    this.tracker.close()
    this.earTracker?.close()
    this.glasses?.dispose()
    this.earrings?.dispose()
    this.faceProps?.dispose()
    this.necklace?.dispose()
    this.rings?.dispose()
    this.wrist?.dispose()
    this.poseTracker?.close()
    this.handTracker?.close()
    this.occluder.dispose()
    this.scene.dispose()
    this.debugOverlay.canvas.remove()
    this.camera.video.remove()
    this.loader.dispose()
    this.emit('destroyed', undefined)
    this.removeAllListeners()
    void this.container
  }
}

/** 0..1 quality: face size in frame (small faces = poor landmarks) — refined with confidence later. */
function faceQuality(face: FaceFrame): number {
  const l = face.landmarks
  // inter-ear distance in normalized width units
  const dx = l[454 * 3]! - l[234 * 3]!
  const dy = l[454 * 3 + 1]! - l[234 * 3 + 1]!
  const size = Math.hypot(dx * face.width, dy * face.height) / Math.max(1, face.height)
  // ~0.12 of frame height = far, 0.5 = close
  return Math.max(0, Math.min(1, (size - 0.08) / 0.3))
}

function defaultResolveProduct(productId: string): ProductDescriptor | null {
  const g = PROCEDURAL_PRODUCTS.find((x) => x.id === productId)
  if (g) return { id: g.id, name: g.name, anchor: g.anchor, procedural: g.procedural }
  const e = PROCEDURAL_EARRINGS.find((x) => x.id === productId)
  if (e) return { id: e.id, name: e.name, anchor: e.anchor, procedural: e.procedural }
  const f = PROCEDURAL_FACE_PROPS.find((x) => x.id === productId)
  if (f) return { id: f.id, name: f.name, anchor: f.anchor, procedural: f.procedural }
  const n = PROCEDURAL_NECKLACES.find((x) => x.id === productId)
  if (n) return { id: n.id, name: n.name, anchor: n.anchor, procedural: n.procedural }
  const r = PROCEDURAL_RINGS.find((x) => x.id === productId)
  if (r) return { id: r.id, name: r.name, anchor: r.anchor, procedural: r.procedural }
  const wp = PROCEDURAL_WRIST.find((x) => x.id === productId)
  return wp ? { id: wp.id, name: wp.name, anchor: wp.anchor, procedural: wp.procedural } : null
}
